import * as d3 from "d3";
import { displayToast, fillColorByDepth, fillAvatarBg } from "../modules/utils";
import screenfull from "screenfull";

class Tree {
  constructor(node) {
    this.element = node;

    // Elements
    this.toggleAllButton = document.querySelector(".js-toggle-all");
    this.toggleAllText = this.toggleAllButton.querySelector(".js-toggle-all-text");
    this.toggleAllIcon = this.toggleAllButton.querySelector(".js-toggle-all-icon");
    this.fullScreenButton = document.querySelector(".js-toggle-fullscreen");
    this.titleElement = this.element.querySelector(".js-tree-title");
    this.headerElement = document.querySelector("#header");

    // Properties
    this.width = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);
    this.height = document.querySelector("main").clientHeight;
    this.centerX = this.width / 2;
    this.root;
    this.tree;
    this.nodeWidth = 294;
    this.nodeHeight = 246;
    this.scale = 1;
    this.i = 0;
    this.scaledWidth = this.nodeWidth * this.scale;
    this.scaledHeight = this.nodeHeight * this.scale;
    this.initialTransform = d3.zoomIdentity.translate(this.centerX - this.nodeWidth / 2, 140);
    this.allExpanded = false;
    this.minScale = 0.1;
    this.maxScale = 1.5;
    this.initialized = false;
    this.isZooming = false;
    this.cssSimplifiedScope = "g.node > *:not(.node-main):not(.node-hover):not(.node-circle)"; // With this selector we remove complex elements from the nodes
    this.minusSvgPath = "M1 1H9";
    this.plusSvgPath = "M1 5H5M5 5H9M5 5V1M5 5V9";
    this.minusSvgTranslation = "translate(249.5, 208)";
    this.plusSvgTranslation = "translate(249.5, 204.5)";

    // Bindings
    this.collapse = this.collapse.bind(this);
    this.openAllNodes = this.openAllNodes.bind(this);
    this.click = this.click.bind(this);
    this.resize = this.resize.bind(this);

    this.fetchData(this.element.dataset.url);
  }

  fetchData(url) {
    this.element.classList.add("is-loading");

    fetch(url)
      .then((response) => response.json())
      .then((data) => {
        // We remove existing nodes

        if (this.svgGroup !== undefined) {
          this.svgGroup.selectAll("*").remove();
        }

        // Incase we expanded everything, set it to false
        if (this.allExpanded) {
          this.toggleAllButton.dispatchEvent(new Event("click"));
        }

        this.buildTree(data);
      })
      .catch((e) => {
        console.error(e);
        displayToast(this.element.dataset.errorMessage);
        window.Appsignal.sendError(e);
      });
  }

  buildTree(data) {
    this.i = 0;
    this.root = d3.hierarchy(data, function (d) {
      return d.children;
    });

    this.totalNodes = this.root.descendants().length;
    const totalNodesObject = this.root.descendants();

    this.toggleAllButton.classList.toggle("d-none", this.totalNodes === 1);

    this.root.each(function (d) {
      d.data.isOpen = false;
    });

    this.root.x0 = 0;
    this.root.y0 = 0;
    this.tree = d3.tree().nodeSize([380, this.width / (this.root.height + 2)]);

    this.collapse(this.root);

    if (!this.initialized) {
      // We add event listeners incase this is the first time we are building the tree
      this.initialize();
    } else {
      this.update(this.root);
    }

    // We always create the image masks and remove them if they exist
    this.svg.selectAll("defs").remove();
    this.createImageMasks(totalNodesObject);
  }

  initialize() {
    window.addEventListener("resize", this.resize);

    this.zoom = d3
      .zoom()
      .filter((event) => {
        if (event.type === "wheel") {
          // Prevent zooming when we are at the min or max scale
          const currentZoomValue = d3.zoomTransform(this.svgGroup.node());

          if (event.deltaY < 0 && currentZoomValue.k === this.minScale) {
            return true;
          }

          if (event.deltaY > 0 && currentZoomValue.k === this.maxScale) {
            return true;
          }

          if (currentZoomValue.k === this.minScale || currentZoomValue.k === this.maxScale) {
            return false;
          }
        }

        if (event.type === "dblclick") {
          // Prevent zooming when we are at the max scale
          const currentZoomValue = d3.zoomTransform(this.svgGroup.node());

          clearTimeout(this.dblclickTimeout);

          if (currentZoomValue.k !== this.maxScale) {
            this.svg.style("pointer-events", "none");
            this.element.style.cursor = "wait";

            this.dblclickTimeout = setTimeout(() => {
              this.reEnableZoom();
            }, 350);

            return true;
          } else {
            return false;
          }
        }

        return true;
      })
      .scaleExtent([this.minScale, this.maxScale])
      .on("start", () => {
        // copy the nodes x and y positions before zooming
        this.svgGroup.selectAll("g.node").each(function (d) {
          d.x0 = d.x;
          d.y0 = d.y;
        });

        this.startZoom = true;
      })
      .on("zoom", (event) => {
        requestAnimationFrame(() => {
          if (this.startZoom) {
            clearTimeout(this.zoomTimeout);

            if (this.shouldTransitionSimplified()) {
              // Only remove the complex nodes if we are zooming with more than 35 nodes
              this.svgGroup.selectAll(this.cssSimplifiedScope).remove();
            }
            this.svgGroup.attr("transform", event.transform);

            this.zoomTimeout = setTimeout(() => {
              // When idle, we end the zoom
              this.zoom.on("end")();
            }, 500);

            this.hasZoomed = true;
          }
        });
      })
      .on("end", () => {
        if (this.startZoom && this.hasZoomed) {
          this.hasZoomed = false;
          this.startZoom = false;

          // reset the nodes x and y positions after zooming
          this.svgGroup.selectAll("g.node").each(function (d) {
            d.x = d.x0;
            d.y = d.y0;
          });

          // update the nodes with the new positions
          this.svgGroup.selectAll("g.node").remove();

          this.update(this.root, { withoutTransition: true });
        }
      });

    this.svg = d3.select(this.element).append("svg").attr("width", this.width).attr("height", this.height).call(this.zoom);

    // Right-click to zoom out
    this.svg.on("contextmenu", (event) => {
      event.preventDefault();

      // Prevent zooming when we are at the min scale
      const currentZoomValue = d3.zoomTransform(this.svgGroup.node());
      clearTimeout(this.zoomOutTimeout);

      if (currentZoomValue.k !== this.minScale) {
        this.svg.style("pointer-events", "none");
        this.element.style.cursor = "wait";

        this.zoomOutTimeout = setTimeout(() => {
          this.reEnableZoom();
        }, 350);

        this.zoomOut();
      }
    });

    this.svgGroup = this.svg.append("g");
    this.svg.call(this.zoom.transform, this.initialTransform);
    this.svgGroup.attr("transform", this.initialTransform.toString());

    this.element.addEventListener("fullscreenchange", () => {
      const svgElement = this.svg.node();

      if (!document.fullscreenElement) {
        svgElement.setAttribute("width", this.width);
        svgElement.setAttribute("height", this.height);
      } else {
        svgElement.setAttribute("width", "100%");
        svgElement.setAttribute("height", "100%");
      }
    });

    this.fullScreenButton.addEventListener("click", () => {
      if (screenfull.isEnabled) {
        screenfull.toggle();
      } else {
        // Fullscreen is unsupported, so we toggle the header instead
        screenfull.isFullscreen = !screenfull.isFullscreen;
        this.toggleHeader();
        window.dispatchEvent(new Event("resize"));
      }
    });

    window.addEventListener("fullscreenchange", () => {
      this.toggleHeader();
    });

    this.toggleAllButton.addEventListener("click", () => {
      this.toggleAllButton.classList.add("disabled", "pe-none");

      if (this.allExpanded) {
        this.closeAllNodes(this.root);

        this.svg.transition().call(this.zoom.transform, this.initialTransform); // We reset the zoom
      } else {
        this.openAllNodes(this.root);

        // We center the root node
        const scale = d3.zoomTransform(this.svg.node()).k;
        const scaledWidth = this.nodeWidth * scale;

        this.svg.transition().call(this.zoom.transform, d3.zoomIdentity.translate(this.centerX - scaledWidth / 2, 140).scale(scale));
      }

      this.updateToggleAllButton();

      this.svgGroup.selectAll(this.cssSimplifiedScope).remove();
      this.update(this.root, { simplifiedTransition: true, updateAfterTransition: true });
    });

    this.element.addEventListener("date-changed", (event) => {
      this.fetchData(event.detail.url);
    });

    this.initialized = true;
  }

  shouldTransitionSimplified() {
    return this.root.descendants().length > 35;
  }

  createImageMasks(totalNodes) {
    // Defining and using a mask for the avatar
    const defs = this.svg.append("defs");

    defs
      .selectAll("pattern")
      .data(totalNodes)
      .enter()
      .append("pattern")
      .attr("id", (d) => `avatar-pattern-${d.data.id}`) // Unique id for the pattern
      .attr("width", "100%")
      .attr("height", "100%")
      .attr("patternContentUnits", "objectBoundingBox")
      .each(function (d) {
        if (d.data.avatar_url !== "") {
          d3.select(this).append("image").attr("xlink:href", d.data.avatar_url).attr("width", 1).attr("height", 1).attr("preserveAspectRatio", "xMidYMid slice");
        } else {
          d3.select(this)
            .append("text")
            .attr("x", 0.5)
            .attr("y", 0.55)
            .attr("text-anchor", "middle")
            .attr("dominant-baseline", "middle")
            .attr("fill", "white")
            .style("font-family", "Figtree")
            .style("font-size", "0.5px")
            .style("font-weight", "700")
            .text(d.data.name.charAt(0));
        }
      });
  }

  reEnableZoom() {
    // Re-enable zoom and pointer events
    this.svg.style("pointer-events", "auto");
    this.element.style.cursor = "";

    this.toggleAllButton.classList.remove("disabled", "pe-none");
  }

  toggleHeader() {
    const headerHeight = this.headerElement.offsetHeight;

    if (screenfull.isFullscreen) {
      this.headerElement.style.position = "absolute";
      this.headerElement.dispatchEvent(
        new CustomEvent("header-toggled", {
          detail: {
            state: "down",
            top: headerHeight,
          },
        })
      );
    } else {
      this.headerElement.style.position = "sticky";
      this.headerElement.dispatchEvent(
        new CustomEvent("header-toggled", {
          detail: {
            state: "up",
            top: 0,
          },
        })
      );
    }
  }

  updateToggleAllButton() {
    this.allExpanded = !this.allExpanded;

    this.toggleAllText.textContent = this.allExpanded ? this.toggleAllButton.dataset.collapseAll : this.toggleAllButton.dataset.expandAll;
    this.toggleAllIcon.className = `icon me-sm-2 js-toggle-all-icon ${this.allExpanded ? "icon-minus" : "icon-plus"}`;
  }

  resize() {
    this.width = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);

    if (screenfull.isFullscreen) {
      this.height = window.innerHeight;
    } else {
      this.height = parseInt(window.getComputedStyle(document.body, null).getPropertyValue("min-height")) - document.querySelector("header").clientHeight;
    }

    this.svg.attr("width", this.width).attr("height", this.height);

    this.centerX = this.width / 2;

    this.initialTransform = d3.zoomIdentity.translate(this.centerX - this.scaledWidth / 2, 140);
    this.svg.call(this.zoom.transform, this.initialTransform);
    this.svgGroup.attr("transform", this.initialTransform.toString());

    this.svgGroup.selectAll(this.cssSimplifiedScope).remove();
    this.update(this.root, { withoutTransition: true, updateAfterTransition: true });
  }

  zoomOut() {
    this.svg.transition().call(this.zoom.scaleBy, 0.5); // Zoom out by 50%
  }

  openAllNodes(node) {
    if (node._children) {
      node.children = node._children;
      node._children = null;
      node.data.isOpen = true;
    }

    if (node.children) {
      node.children.forEach((child) => this.openAllNodes(child));
    }
  }

  closeAllNodes(node) {
    if (node.children) {
      node.children.forEach((child) => this.closeAllNodes(child));
      node._children = node.children;
      node.children = null;
      node.data.isOpen = false;
    }
  }

  collapse(d) {
    if (d.children) {
      d._children = d.children;
      d._children.forEach((child) => this.collapse(child));
      d.children = null;
    }
  }

  buildMainGroup(node, source, simplified = false) {
    const nodeEnter = node
      .enter()
      .append("g")
      .attr("data-id", (d) => d.data.id)
      .attr("class", (d) => (Array.isArray(d.data.children) && d.data.children.length > 0 ? "node" : "node disabled"))
      .attr("transform", () => `translate(${source.x0},${source.y0})`);

    return !simplified ? nodeEnter : nodeEnter.lower();
  }

  buildNode(node, source, simplified = false) {
    const nodeEnter = this.buildMainGroup(node, source, simplified);

    // Main shape
    nodeEnter
      .append("path")
      .attr("d", "M0 64C0 55.1634 7.16344 48 16 48H278C286.837 48 294 55.1634 294 64V174H0V64Z")
      .attr("fill", "white")
      .attr("stroke", "#EEEEEE")
      .attr("class", "node-main");

    // Bottom shape
    nodeEnter
      .append("path")
      .attr("d", "M0 0H294V56C294 64.8366 286.837 72 278 72H16C7.16345 72 0 64.8366 0 56V0Z")
      .attr("transform", "translate(0, 174)")
      .attr("fill", "white")
      .attr("stroke", "#EEEEEE")
      .attr("class", "node-hover")
      .on("mousedown", (event) => {
        // Prevent zooming when clicking on a node
        event.stopPropagation();
      })
      .on("mouseup", (event, d) => {
        event.stopPropagation();
        this.click(event, d);
      })
      .on("touchstart", (event) => {
        event.stopPropagation();
      })
      .on("touchend", (event, d) => {
        event.stopPropagation();
        this.click(event, d);
      });

    // Circle fallback shape
    nodeEnter
      .append("circle")
      .attr("cx", 147)
      .attr("cy", 40)
      .attr("r", 40)
      .attr("fill", (d) => fillAvatarBg(d.data.avatar_number))
      .attr("class", "node-circle");

    if (simplified) {
      return nodeEnter;
    }

    // Title
    nodeEnter
      .append("text")
      .attr("x", 147)
      .attr("y", 114.784)
      .attr("text-anchor", "middle")
      .attr("fill", "#1F1F1F")
      .style("font-family", "Mukta")
      .style("font-size", "16px")
      .style("font-weight", "500")
      .html((d) => `<a href="${d.data.url}" target="_blank" class="text-decoration-none">${d.data.name} • ${d.data.PGV}</a>`)
      .on("mousedown", (event) => {
        // Prevent zooming when clicking on a node
        event.stopPropagation();
      })
      .on("mouseup", (event) => {
        event.stopPropagation();
      })
      .on("touchstart", (event) => {
        event.stopPropagation();
      })
      .on("touchend", (event) => {
        event.stopPropagation();
      });

    // Subtitle
    nodeEnter
      .append("text")
      .attr("x", 145)
      .attr("y", 144)
      .attr("text-anchor", "middle")
      .attr("fill", "#4C4C4E")
      .style("font-family", "Mukta")
      .style("font-size", "14px")
      .style("font-weight", "400")
      .text((d) => `${d.data.QVLevel} • ${d.data.id}`);

    // Toggle text
    nodeEnter
      .append("text")
      .attr("x", 24)
      .attr("y", 213.784)
      .attr("text-anchor", "start")
      .style("font-family", "Mukta")
      .style("font-size", "14px")
      .style("font-weight", "400")
      .attr("class", (d) => (Array.isArray(d.data.children) && d.data.children.length > 0 ? "toggle-text" : "toggle-text text-disabled"))
      .text((d) => (d.data.isOpen ? this.element.dataset.hideChildren : this.element.dataset.showChildren));

    // Circle shape
    nodeEnter
      .append("circle")
      .attr("cx", 147)
      .attr("cy", 40)
      .attr("r", 40)
      .attr("fill", (d) => `url(#avatar-pattern-${d.data.id}`);

    // Depth shape
    nodeEnter
      .append("rect")
      .attr("x", 163)
      .attr("y", 56)
      .attr("width", 24)
      .attr("height", 24)
      .attr("rx", 12)
      .attr("fill", (d) => fillColorByDepth(d.data.depth))
      .attr("stroke", "#ffffff");

    // Depth number
    nodeEnter
      .append("text")
      .attr("x", 175)
      .attr("y", 72.784)
      .attr("text-anchor", "middle")
      .style("fill", "white")
      .style("font-family", "Mukta")
      .style("font-size", "16px")
      .attr("class", "depth-text")
      .text((d) => d.data.depth);

    // Toggle button
    nodeEnter
      .append("rect")
      .attr("x", 238.5)
      .attr("y", 194)
      .attr("width", 31)
      .attr("height", 31)
      .attr("rx", 7.5)
      .attr("fill", "white")
      .attr("stroke", "#EEEEEE")
      .attr("class", "toggle-button");

    // Toggle icon
    nodeEnter
      .append("path")
      .attr("d", (d) => (Array.isArray(d.data.children) && d.data.children.length > 0 && d.data.isOpen ? this.minusSvgPath : this.plusSvgPath))
      .attr("height", 8)
      .attr("width", 8)
      .attr("stroke", "#1F1F1F")
      .attr("stroke-width", "1.5")
      .attr("stroke-linecap", "round")
      .attr("stroke-linejoin", "round")
      .attr("class", "toggle-icon")
      .attr("transform", (d) => {
        return Array.isArray(d.data.children) && d.data.children.length > 0 && d.data.isOpen ? this.minusSvgTranslation : this.plusSvgTranslation;
      });

    return nodeEnter;
  }

  update(source, options = { withoutTransition: false, simplifiedTransition: false, updateAfterTransition: false }) {
    this.svg.style("pointer-events", "none");
    this.element.style.cursor = "wait";

    const treeData = this.tree(this.root);
    const descendantNodes = treeData.descendants();
    const links = treeData.links();

    // Space between levels and between nodes
    descendantNodes.forEach((d) => {
      d.y = d.depth * 300;
      d.x = d.x * 0.8325;
    });

    let existingNodes = this.svgGroup.selectAll("g.node");
    existingNodes = existingNodes.data(descendantNodes, (d) => d.id || (d.id = ++this.i));

    this.titleElement.classList.toggle("opacity-0", descendantNodes.length > 1);

    // Remove any exiting nodes
    if (options.withoutTransition) {
      existingNodes.exit().remove();
    } else {
      existingNodes
        .exit()
        .transition()
        .duration(350)
        .attr("transform", () => `translate(${source.x},${source.y})`)
        .remove();
    }

    let nodeUpdate;
    if (options.simplifiedTransition) {
      nodeUpdate = this.buildNode(existingNodes, source, true).merge(existingNodes);
    } else {
      nodeUpdate = this.buildNode(existingNodes, source).merge(existingNodes);
    }

    if (options.withoutTransition) {
      nodeUpdate.attr("transform", (d) => `translate(${d.x},${d.y})`);
      this.reEnableZoom();
    } else {
      nodeUpdate
        .transition()
        .duration(350)
        .attr("transform", (d) => `translate(${d.x},${d.y})`);
    }

    // Update the links
    const link = this.svgGroup.selectAll("path.link").data(links, (d) => d.target.id);

    const linkEnter = link
      .enter()
      .insert("path", "g")
      .attr("class", "link")
      .attr("d", () => this.diagonal({ x: source.x0, y: source.y0 }, { x: source.x0, y: source.y0 }))
      .style("stroke-width", "1px")
      .style("stroke", "#CACACA")
      .style("fill", "none");

    const linkUpdate = linkEnter.merge(link);

    if (options.withoutTransition) {
      linkUpdate.attr("d", (d) => this.diagonal(d.source, d.target));
    } else {
      // Transition back to the parent element position
      linkUpdate
        .transition()
        .duration(350)
        .attr("d", (d) => this.diagonal(d.source, d.target));
    }

    if (options.withoutTransition) {
      link.exit().remove();
    } else {
      // Remove any exiting links
      link
        .exit()
        .transition()
        .duration(350)
        .attr("d", () => this.diagonal({ x: source.x, y: source.y }, { x: source.x, y: source.y }))
        .remove();
    }

    // Store the old positions for transition.
    descendantNodes.forEach((d) => {
      d.x0 = d.x;
      d.y0 = d.y;
    });

    clearTimeout(this.transitionTimeout);
    // We wait for the transitions to finish before re-enabling zoom
    this.transitionTimeout = setTimeout(() => {
      if (options.updateAfterTransition) {
        this.svgGroup.selectAll("g.node").remove();

        this.update(source, { withoutTransition: true });
      } else {
        this.reEnableZoom();
      }
    }, 350);

    this.element.classList.remove("is-loading");
  }

  click(event, d) {
    if (event.target.tagName === "a" || d.children === undefined) {
      return;
    }

    if (this.shouldTransitionSimplified()) {
      // We must remove all complex nodes children.
      this.svgGroup.selectAll(this.cssSimplifiedScope).remove();
    }

    if (d.children) {
      d._children = d.children;
      d.children = null;
      d.data.isOpen = false; // Node is being closed
    } else {
      d.children = d._children;
      d._children = null;
      d.data.isOpen = true; // Node is being opened
    }

    const node = d3.select(event.currentTarget);
    const isOpen = !!d.children;

    // Update text and icon
    node.select(".toggle-text").text(isOpen ? this.element.dataset.hideChildren : this.element.dataset.showChildren);
    node.select(".toggle-icon").attr("d", isOpen ? this.minusSvgPath : this.plusSvgPath);
    node.select(".toggle-icon").attr("transform", isOpen ? this.minusSvgTranslation : this.plusSvgTranslation);

    if (d.children) {
      this.update(d, { simplifiedTransition: this.shouldTransitionSimplified(), updateAfterTransition: true });
    } else {
      this.update(d, { updateAfterTransition: true });
    }

    const treeData = this.tree(this.root);
    const nodes = treeData.descendants();

    if (nodes.length === 1) {
      // Incase we only have the root node shown
      this.allExpanded = true;
      this.updateToggleAllButton();
    }

    if (nodes.length === this.totalNodes) {
      // Check if all nodes are expanded
      this.allExpanded = false;
      this.updateToggleAllButton();
    }

    this.centerNode(d);
  }

  centerNode(source) {
    // center to the node that was clicked
    const scale = d3.zoomTransform(this.svg.node()).k;
    const scaledWidth = this.nodeWidth * scale;

    const x = -source.x0 * scale + this.centerX - scaledWidth / 2;
    const y = -source.y0 * scale + 140;

    this.svg.transition().duration(350).call(this.zoom.transform, d3.zoomIdentity.translate(x, y).scale(scale));
  }

  diagonal(s, t) {
    const x = s.x + this.scaledWidth / 2; // Adjust start x
    const y = s.y + this.scaledHeight / 2; // Adjust start y
    const ex = t.x + this.scaledWidth / 2; // Adjust target x
    const ey = t.y + this.scaledHeight / 2; // Adjust target y

    let xrvs = ex - x < 0 ? -1 : 1;
    let yrvs = ey - y < 0 ? -1 : 1;

    let rdef = 35;
    let r = Math.abs(ex - x) / 2 < rdef ? Math.abs(ex - x) / 2 : rdef;

    r = Math.abs(ey - y) / 2 < r ? Math.abs(ey - y) / 2 : r;

    let h = Math.abs(ey - y) / 2 - r;
    let w = Math.abs(ex - x) - r * 2;
    return `
        M ${x} ${y}
        L ${x} ${y + h * yrvs}
        C  ${x} ${y + h * yrvs + r * yrvs} ${x} ${y + h * yrvs + r * yrvs} ${x + r * xrvs} ${y + h * yrvs + r * yrvs}
        L ${x + w * xrvs + r * xrvs} ${y + h * yrvs + r * yrvs}
        C  ${ex}  ${y + h * yrvs + r * yrvs} ${ex}  ${y + h * yrvs + r * yrvs} ${ex} ${ey - h * yrvs}
        L ${ex} ${ey}
  `;
  }
}

const treeElement = document.querySelector("[data-js='tree']");
if (treeElement) {
  new Tree(treeElement);
}

const treeFilter = document.querySelector("[data-js='tree-filter']");
if (treeFilter) {
  const datePickerInput = treeFilter.querySelector(".js-tree-filter-date");

  datePickerInput.addEventListener("datepicker-change", (event) => {
    const date = event.target.value;
    const url = new URL(treeElement.dataset.url, window.location.origin);

    url.searchParams.set("date", date);

    treeElement.dispatchEvent(
      new CustomEvent("date-changed", {
        detail: {
          url: url,
        },
      })
    );
  });
}
